import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client/react';
import { ChipList } from '@progress/kendo-react-buttons';
import cn from 'clsx';
import { PanelPreview } from '../panel-preview/panel-preview';
import {
    BenchmarkType,
    CountryType,
    IFormData,
    OptionsEventRatesOT,
    RowDataDate,
    subjectTables,
    TimePointDate,
} from './interfaces';
import styles from './style.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import Card from '@components-clean/core/card';
import { CheckboxController } from '@components-clean/core/core-checkbox';
import EditCard from '@components-clean/core/edit-card';
import Loading from '@components-clean/core/loading/loading';
import { MultiSelectController } from '@components-clean/core/multi-select';
import { TextBoxController } from '@components-clean/core/text-box';
import { NumericTextBoxController } from '@components-clean/core/text-box-numeric';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { FieldDefinitionFieldTypeDto, RowDataSourceTableDto } from '@interfaces-clean/field-rdl-definition.interface';
import { usePanelDetailsContext } from '@pages-clean/admin/study/manage-panels/context';
import { COMPONENT_NAME, ICreateData } from '@pages-clean/admin/study/manage-panels/tab-content/panel-details/shared';
import PanelReasonForChangeDialog from '@pages-clean/admin/study/manage-panels/tab-content/panel-details/types/panel-reason-for-change-dialog';
import {
    AutomationRange,
    EventRatesOTBenchmarkType,
    EventRatesOTCountryBenchmarkType,
    EventRatesOTThresholdType,
    ModelPlotCalculation,
    ModelScaleBy,
    PanelInput,
    PanelType,
    RowDataSourceTag,
} from 'types/graphql-global-types';
import { useLocalState, useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtcNonNullable } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { GET_PANEL, GET_PANELS, SAVE_PANEL } from '@queries/central-monitoring';
import {
    getPanels,
    panel as IPanel,
    panel_OpraQueries_panel_Panel,
    panelVariables as IPanelVariables,
    upsertPanel,
    upsertPanelVariables,
} from '@queries/generated/central-monitoring.generated';
import { useGetLatestDataSet } from '@apis/bff-api/central-monitoring';

interface IEventRatesProps {
    panel?: panel_OpraQueries_panel_Panel;
    onCancelOverride?: () => void;
    onUpdate: () => void;
    onCreate: (panelRef: string) => void;
    canEdit?: boolean;
    processedConfig?: panel_OpraQueries_panel_Panel;
}

interface IProps {
    panelRef?: string;
    createData?: ICreateData;
    canEdit?: boolean;
}

enum ChipValues {
    Configuration = 1,
    Preview = 2,
}
const EventRatesOT = ({ panel, onCancelOverride, onUpdate, onCreate, canEdit, processedConfig }: IEventRatesProps) => {
    const { t } = useTranslation('admin', { keyPrefix: 'managePanels.panelDetails.eventRatesOT' });
    const { t: tOptions } = useTranslation('admin', { keyPrefix: 'managePanels.panelDetails.eventRatesOT.options' });
    const { t: tCommon } = useTranslation('common');
    const { t: tManagePanels } = useTranslation('admin', { keyPrefix: 'managePanels' });
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();
    const [chip, setChip] = useState<number>(ChipValues.Configuration);
    const { source } = usePanelDetailsContext();
    const [showReasonForChange, setShowReasonForChange] = useState(false);

    const {
        ScaleByOptions,
        PlotByOptions,
        ThresholdTypeOptions,
        BenchmarkTypeOptions,
        CountryBenchmarkTypeOptions,
        PlotCalculationOptions,
        MinTimeOnStudyPeriodOptions,
        YesNoOptions,
        ScaleTimePointOptions,
        RangeOptions,
    } = OptionsEventRatesOT(tOptions);

    const [savePanel, { loading: isSaving }] = useMutation<upsertPanel, upsertPanelVariables>(SAVE_PANEL);
    const { state } = useLocalState();

    const { data, loading: isLoading } = useQuery<getPanels>(GET_PANELS, {
        variables: {
            organisationId: state?.selectedOrganisation?.id,
            studyId: state?.selectedStudy?.id,
        },
        fetchPolicy: 'no-cache',
    });

    const studyPanels = useMemo(() => {
        return data?.panels?.flatMap((studyPanel) => {
            if (!!studyPanel?.plotTitle && studyPanel.id !== panel?.id) return [studyPanel?.plotTitle];
            return [];
        });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data]);

    const rowDataOptions = state?.cm2DataSources
        ?.filter((x) => subjectTables.includes(x.table))
        ?.flatMap((d) => {
            if (d?.id == null || d?.name == null || d.table == null) return [];

            if (!d?.tags?.includes(RowDataSourceTag.PANEL) || d?.default) return [];

            return [
                {
                    id: d?.id,
                    name: d?.name,
                    table: d.table,
                },
            ];
        })
        .sort((a, b) => a.name.localeCompare(b.name));

    const timePointsLListOptions = state?.cm2DataSources
        ?.filter(
            (x) =>
                x.table === RowDataSourceTableDto.SUBJECT_VISIT || x.table === RowDataSourceTableDto.SUBJECT_ASSESSMENT,
        )
        ?.flatMap((d) => {
            if (d?.id == null || d?.name == null || d.table == null) return [];

            if (!d?.tags?.includes(RowDataSourceTag.PANEL) || d?.default) return [];

            return [
                {
                    id: d?.id,
                    name: d?.name,
                    table: d.table,
                },
            ];
        })
        .sort((a, b) => a.name.localeCompare(b.name));

    const getFieldOptions = (table: string, fieldTypes: FieldDefinitionFieldTypeDto[]) => {
        return state?.cm2Fields
            ?.filter((f) => {
                if (f?.fieldType == null) return false;
                if (table !== f?.dataType) return false;
                if (f.isHidden) return false;

                return fieldTypes.includes(f?.fieldType);
            })
            .flatMap((f) => {
                if (f?.key == null || f.name == null) return [];

                return [
                    {
                        id: f?.key,
                        name: f.name,
                    },
                ];
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    };

    const getDateOptions = (table: string) =>
        getFieldOptions(table, [FieldDefinitionFieldTypeDto.DATE, FieldDefinitionFieldTypeDto.PARTIAL_DATE]);

    const [rowDataDateOptions, setRowDataDateOptions] = useState<RowDataDate[] | undefined>(() =>
        getDateOptions(rowDataOptions?.find((r) => r.id === panel?.eventRatesOverTimeParameters?.rowData)?.table || ''),
    );

    const [timepointDateOptions, setTimepointDateOptions] = useState<TimePointDate[] | undefined>(() =>
        getDateOptions(
            timePointsLListOptions?.find((r) => r.id === panel?.eventRatesOverTimeParameters?.timepointsList)?.table ||
                '',
        ),
    );

    const [updatedBenchMarkOptions, setUpdatedBenchMarkOptions] = useState<BenchmarkType[] | undefined>(
        BenchmarkTypeOptions,
    );

    const [updatedCountryTypeOptions, setUpdatedCountryTypeOptions] = useState<CountryType[] | undefined>(
        CountryBenchmarkTypeOptions,
    );

    const defaultFormValues = useMemo<IFormData>(() => {
        const rowData =
            (panel?.eventRatesOverTimeParameters?.rowData &&
                rowDataOptions?.find((r) => r.id === panel?.eventRatesOverTimeParameters?.rowData)) ||
            null;

        const timepointsList =
            (panel?.eventRatesOverTimeParameters?.timepointsList &&
                timePointsLListOptions?.find((r) => r.id === panel?.eventRatesOverTimeParameters?.timepointsList)) ||
            null;

        const isRateByTimePoint = ScaleTimePointOptions.map((i) => i.id).includes(
            panel?.eventRatesOverTimeParameters?.scaleBy as ModelScaleBy,
        );

        const isRateByPeriod = ScaleByOptions.map((i) => i.id).includes(
            panel?.eventRatesOverTimeParameters?.scaleBy as ModelScaleBy,
        );

        return {
            xAxisTitle: panel?.eventRatesOverTimeParameters?.xAxisTitle ?? '',
            yAxisTitle: panel?.eventRatesOverTimeParameters?.yAxisTitle ?? '',
            isActive: panel?.active ?? true,
            configId: panel?.configId,
            plotTitle: panel?.name ?? '',
            rowData,
            qtl:
                (panel?.eventRatesOverTimeParameters?.qtl && YesNoOptions.find((o) => o.id === 'true')) ||
                YesNoOptions.find((o) => o.id === 'false'),
            reportOOT: panel?.eventRatesOverTimeParameters?.reportOutOfTolerance
                ? YesNoOptions.find((o) => o.id === 'true')
                : panel?.eventRatesOverTimeParameters?.reportOutOfTolerance === false
                  ? YesNoOptions.find((o) => o.id === 'false')
                  : null,
            rowDataDate:
                (rowData &&
                    panel?.eventRatesOverTimeParameters?.rowDataDate &&
                    getDateOptions(rowData.table)?.find(
                        (s) => s.id === panel?.eventRatesOverTimeParameters?.rowDataDate,
                    )) ||
                null,
            rateBy: isRateByPeriod ? t('options.PERIOD') : isRateByTimePoint ? t('options.TIMEPOINTS') : null,
            scaleBy:
                (panel?.eventRatesOverTimeParameters?.scaleBy &&
                    ScaleByOptions.find((o) => o.id === panel?.eventRatesOverTimeParameters?.scaleBy)) ||
                ScaleByOptions.find((o) => o.id === ModelScaleBy.PER_SUBJECT_YEAR),
            scaleTimepoint:
                (panel?.eventRatesOverTimeParameters?.scaleBy &&
                    ScaleTimePointOptions.find((o) => o.id === panel?.eventRatesOverTimeParameters?.scaleBy)) ||
                ScaleTimePointOptions.find((o) => o.id === ModelScaleBy.PER_SUBJECT_TIME_POINT),
            timepointsList,
            timepointDate:
                (timepointsList &&
                    panel?.eventRatesOverTimeParameters?.timepointDate &&
                    getDateOptions(timepointsList.table)?.find(
                        (s) => s.id === panel?.eventRatesOverTimeParameters?.timepointDate,
                    )) ||
                null,
            plotBy:
                (panel?.eventRatesOverTimeParameters?.plotBy &&
                    PlotByOptions.find((o) => o.id === panel?.eventRatesOverTimeParameters?.plotBy)) ||
                null,
            plotCalculations:
                (panel?.eventRatesOverTimeParameters?.cumulativeOrTimePeriod &&
                    PlotCalculationOptions.find(
                        (o) => o.id === panel?.eventRatesOverTimeParameters?.cumulativeOrTimePeriod,
                    )) ||
                null,
            calcStartDate:
                (panel?.eventRatesOverTimeParameters?.calcStartDate &&
                    getDateOptions(RowDataSourceTableDto.SUBJECT)?.find(
                        (s) => s.id === panel?.eventRatesOverTimeParameters?.calcStartDate,
                    )) ||
                null,
            calcEndDates:
                panel?.eventRatesOverTimeParameters?.calcEndDates
                    ?.map((d) => {
                        const fieldName = getDateOptions(RowDataSourceTableDto.SUBJECT)?.find((s) => s.id === d)?.name;
                        return {
                            id: d,
                            name: fieldName,
                            active: fieldName !== undefined && fieldName !== null,
                        };
                    })
                    .filter((f) => f.active) ?? null,

            xAxisPtsToShade: panel?.eventRatesOverTimeParameters?.xAxisPtsToShade ?? null,
            thresholdType:
                (panel?.eventRatesOverTimeParameters?.thresholdType &&
                    ThresholdTypeOptions.find((o) => o.id === panel?.eventRatesOverTimeParameters?.thresholdType)) ||
                null,
            upperRed: panel?.eventRatesOverTimeParameters?.upperRed ?? null,
            upperAmber: panel?.eventRatesOverTimeParameters?.upperAmber ?? null,
            lowerRed: panel?.eventRatesOverTimeParameters?.lowerRed ?? null,
            lowerAmber: panel?.eventRatesOverTimeParameters?.lowerAmber ?? null,
            minSubjects: panel?.eventRatesOverTimeParameters?.minSubjects ?? null,
            minTimeUnit: panel?.eventRatesOverTimeParameters?.minTimeOnStudyUnit ?? null,
            minTimePeriod:
                (panel?.eventRatesOverTimeParameters?.minTimeOnStudyPeriod &&
                    MinTimeOnStudyPeriodOptions.find(
                        (o) => o.id === panel?.eventRatesOverTimeParameters?.minTimeOnStudyPeriod,
                    )) ||
                null,
            studyType:
                (panel?.eventRatesOverTimeParameters?.thresholdType &&
                    BenchmarkTypeOptions.find((o) => o.id === panel?.eventRatesOverTimeParameters?.benchmarkType)) ||
                null,
            studySpecificValue: panel?.eventRatesOverTimeParameters?.studySpecificBenchmark ?? null,
            countryValue:
                (panel?.eventRatesOverTimeParameters?.countryBenchmark &&
                    CountryBenchmarkTypeOptions.find(
                        (o) => o.id === panel?.eventRatesOverTimeParameters?.countryBenchmark,
                    )) ||
                null,
            numeratorName: panel?.eventRatesOverTimeParameters?.numeratorName ?? null,
            denominatorName: panel?.eventRatesOverTimeParameters?.denominatorName ?? null,
            expectedVolume: panel?.eventRatesOverTimeParameters?.expectedVolume ?? null,
            numberOfTimePeriods: panel?.eventRatesOverTimeParameters?.numberOfTimePeriods ?? null,
            automationRange:
                (panel?.eventRatesOverTimeParameters?.automationRange &&
                    RangeOptions.find((o) => o.id === panel?.eventRatesOverTimeParameters?.automationRange)) ||
                null,
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [panel]);

    const {
        control,
        reset,
        setValue,
        handleSubmit,
        getValues,
        watch,
        trigger,
        formState: { isDirty, isValid },
    } = useForm<IFormData>({
        defaultValues: defaultFormValues,
        mode: 'onChange',
    });

    const [isEditing, setIsEditing] = useState(!panel);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isDirty]);

    const disableThresholds = useMemo(() => {
        return getValues('thresholdType') == null;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('thresholdType')]);

    const disableRowDataFields = useMemo(() => {
        return getValues('rowData') == null;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('rowData')]);

    const disableTimePointDateField = useMemo(() => {
        return getValues('timepointsList') == null;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('timepointsList')]);

    const disableCountryType = useMemo(() => {
        return getValues('studyType') == null;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('studyType')]);

    const showStudySpecific = useMemo(() => {
        const benchMarkType = getValues('studyType');
        return benchMarkType?.id === EventRatesOTBenchmarkType.STUDY_SPECIFIC;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('studyType')]);

    const isRateByTimePoints = useMemo(() => {
        const rateBy = getValues('rateBy');
        return rateBy === t('options.TIMEPOINTS');
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('rateBy')]);

    const isRateByPeriod = useMemo(() => {
        const rateBy = getValues('rateBy');
        return rateBy === t('options.PERIOD');
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('rateBy')]);

    const disableAutomationRange = useMemo(() => {
        const plotCalculations = getValues('plotCalculations');
        return plotCalculations?.id !== ModelPlotCalculation.BY_TIME_PERIOD;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('plotCalculations')]);

    const enableNoOfTimePeriods = useMemo(() => {
        const range = getValues('automationRange');
        const plotCalculations = getValues('plotCalculations');
        return (
            range?.id === AutomationRange.SPECIFIC_RANGE && plotCalculations?.id === ModelPlotCalculation.BY_TIME_PERIOD
        );
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('automationRange')]);

    const submit = async (reasonForChange?: string) => {
        await handleSubmit(async (formData) => {
            try {
                if (!isValid) return;

                // @ts-ignore
                const savingPanel: PanelInput = {
                    id: panel?.id,
                    configId: panel?.configId,

                    organisationId: state?.organisation?.id,
                    studyId: state?.selectedStudy?.id,
                    panelType: PanelType.EVENT_RATES_OVER_TIME,

                    name: formData.plotTitle,
                    // TODO: Once we work out how to move to the Name field exclusively, remove this duplication
                    graph: {
                        plotTitle: formData.plotTitle,
                    },

                    active: formData.isActive,

                    eventRatesOverTimeParameters: {
                        qtl: formData.qtl?.id === 'true',
                        reportOutOfTolerance: formData.reportOOT?.id === 'true',
                        xAxisTitle: formData.xAxisTitle,
                        yAxisTitle: formData.yAxisTitle,
                        rowData: formData.rowData?.id,
                        rowDataDate: formData.rowDataDate?.id,
                        scaleBy:
                            formData.rateBy === t('options.TIMEPOINTS')
                                ? formData.scaleTimepoint?.id
                                : formData.scaleBy?.id,
                        timepointsList: formData.timepointsList?.id,
                        timepointDate: formData.timepointDate?.id,
                        plotBy: formData.plotBy?.id,
                        calcStartDate: formData.calcStartDate?.id,
                        calcEndDates: formData.calcEndDates?.map((i) => i.id),
                        cumulativeOrTimePeriod: formData.plotCalculations?.id,
                        thresholdType: formData.thresholdType?.id,
                        upperRed: formData.upperRed,
                        upperAmber: formData.upperAmber,
                        lowerRed: formData.lowerRed,
                        lowerAmber: formData.lowerAmber,
                        xAxisPtsToShade: formData.xAxisPtsToShade,
                        expectedVolume: formData.expectedVolume,
                        minSubjects: formData.minSubjects,
                        minTimeOnStudyUnit: formData.minTimeUnit,
                        minTimeOnStudyPeriod: formData.minTimePeriod?.id,
                        benchmarkType: formData.studyType?.id,
                        studySpecificBenchmark: formData.studySpecificValue,
                        countryBenchmark: formData.countryValue?.id,
                        numeratorName: formData.numeratorName,
                        denominatorName: formData.denominatorName,
                        numberOfTimePeriods: formData.numberOfTimePeriods,
                        automationRange: formData.automationRange?.id,
                    },
                    associatedRisks: panel?.associatedRisksIds,
                };

                const result = await savePanel({
                    variables: {
                        panel: savingPanel,
                        reason: reasonForChange ?? '',
                    },
                });

                if (result.data?.upsertPanel == null) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }

                ShowSuccess(tCommon('actionsSuccessfulToast'));
                setIsEditing(false);

                if (panel) {
                    onUpdate();
                } else {
                    await onCreate(result.data?.upsertPanel?.id!);
                }
            } catch (error) {
                ShowError(tCommon('errorMessage'));
                throw error;
            }
        })();
    };

    const updateChip = (tabIndex: number) => {
        if (tabIndex !== null) {
            setChip(tabIndex);
        }
    };

    // If any new field is added in the panel config that affects the panel preview, then it needs to be added here too
    // as then we need it for preview.

    const currentPanel = getValues();
    const currentConfig = {
        ...panel,
        plotTitle: currentPanel?.plotTitle,
        panelType: PanelType.EVENT_RATES_OVER_TIME,
        __typename: 'Panel' as const,
        eventRatesOverTimeParameters: {
            benchmarkType: currentPanel.studyType?.id,
            calcStartDate: currentPanel.calcStartDate?.id,
            calcEndDates: currentPanel.calcEndDates?.map((i) => i.id),
            countryBenchmark: currentPanel.countryValue?.id,
            cumulativeOrTimePeriod: currentPanel.plotCalculations?.id,
            denominatorName: currentPanel.denominatorName,
            expectedVolume: currentPanel.expectedVolume,
            lowerAmber: currentPanel.lowerAmber,
            lowerRed: currentPanel.lowerRed,
            minSubjects: currentPanel.minSubjects,
            minTimeOnStudyPeriod: currentPanel.minTimePeriod?.id,
            minTimeOnStudyUnit: currentPanel.minTimeUnit,
            numeratorName: currentPanel.numeratorName,
            plotBy: currentPanel.plotBy?.id,
            qtl: currentPanel.qtl?.id,
            reportOutOfTolerance: currentPanel.reportOOT?.id,
            rowData: currentPanel.rowData?.id,
            rowDataDate: currentPanel.rowDataDate?.id,
            scaleBy:
                currentPanel.rateBy === t('options.TIMEPOINTS')
                    ? currentPanel.scaleTimepoint?.id
                    : currentPanel.scaleBy?.id,
            studySpecificBenchmark: currentPanel.studySpecificValue,
            thresholdType: currentPanel.thresholdType?.id,
            upperRed: currentPanel.upperRed,
            upperAmber: currentPanel.upperAmber,
            xAxisPtsToShade: currentPanel.xAxisPtsToShade,
            xAxisTitle: currentPanel.xAxisTitle,
            yAxisTitle: currentPanel.yAxisTitle,
            timepointsList: currentPanel.timepointsList?.id,
            timepointDate: currentPanel.timepointDate?.id,
            numberOfTimePeriods: currentPanel.numberOfTimePeriods,
            automationRange: currentPanel.automationRange?.id,
        },
    };

    const content = (
        <div className="k-h-full">
            <div className="k-pt-2 k-pl-2 k-pr-2 k-mb-3">
                <Card data-testid="event-rates-ot-panel-preview" className="k-min-h-min" bodyClassName="!k-p-1">
                    <ChipList
                        data-testid="panel-preview"
                        defaultData={[
                            { text: tManagePanels('configuration'), value: ChipValues.Configuration },
                            { text: tManagePanels('preview'), value: ChipValues.Preview },
                        ]}
                        value={chip}
                        onChange={(event) => {
                            updateChip(event.value as number);
                        }}
                        selection="single"
                    />
                </Card>
            </div>

            {chip === ChipValues.Configuration && (
                <div className={cn(styles.eventRatesContainer)}>
                    <div className={cn(styles.eventRates)}>
                        {/* COLUMN 1 */}
                        <div
                            className={cn(
                                styles.borderRight,
                                'k-d-flex-col k-pr-3 k-pl-3 k-py-3 k-col-span-1 k-overflow-y-auto',
                            )}
                        >
                            <div className="k-d-flex-row k-align-items-center">
                                <div className="k-mr-4 k-min-w-fit">
                                    <CheckboxController
                                        data-testid="is-active"
                                        label={t('labels.active')}
                                        name="isActive"
                                        control={control}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div className={cn(styles.borderLeft, 'k-pl-4 k-flex-grow')}>
                                    <TextBoxController
                                        label={t('labels.configId')}
                                        data-testid="config-id"
                                        name="configId"
                                        control={control}
                                        readOnly
                                        copyBtn
                                    />
                                </div>
                            </div>
                            <div>
                                <TextBoxController
                                    label={t('labels.plotTitle')}
                                    data-testid="plot-title"
                                    name="plotTitle"
                                    control={control}
                                    required
                                    maxLength={50}
                                    readOnly={!isEditing}
                                    validateRules={{
                                        uniqueTitle: (value) => {
                                            const plotTitle = value as string;
                                            if (plotTitle != null && studyPanels?.includes(plotTitle))
                                                return t('validation.uniquePlotTitle');
                                        },
                                    }}
                                />
                            </div>
                            <div className="k-mt--2">
                                <TextBoxController
                                    label={t('labels.xAxisTitle')}
                                    data-testid="x-axis-title"
                                    name="xAxisTitle"
                                    control={control}
                                    required
                                    maxLength={50}
                                    readOnly={!isEditing}
                                />
                            </div>
                            <div className="k-mt--2">
                                <TextBoxController
                                    label={t('labels.yAxisTitle')}
                                    data-testid="y-axis-title"
                                    name="yAxisTitle"
                                    control={control}
                                    required
                                    readOnly={!isEditing}
                                    maxLength={50}
                                />
                            </div>

                            <div className="k-d-flex-row k-gap-3 k-justify-content-evenly k-w-full">
                                <BasicDropDownController
                                    label={t('labels.qtl')}
                                    control={control}
                                    name="qtl"
                                    data-testid="qtl"
                                    data={YesNoOptions}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                />

                                <BasicDropDownController
                                    label={t('labels.reportoot')}
                                    control={control}
                                    name="reportOOT"
                                    data-testid="report-oot"
                                    data={YesNoOptions}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                />
                            </div>
                        </div>
                        {/* COLUMN 2 */}
                        <div
                            className={cn(
                                styles.borderRight,
                                'k-d-flex-col k-gap-3 k-justify-content-start k-overflow-y-auto k-pb-3 k-px-4 k-overflow-y-auto',
                            )}
                        >
                            <div className="k-d-flex-row k-mt-3">
                                <BasicDropDownController
                                    label={t('labels.rowData')}
                                    control={control}
                                    name="rowData"
                                    data-testid="row-data"
                                    data={rowDataOptions || []}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                    onChange={async (event) => {
                                        const { value } = event;
                                        if (value) {
                                            const rowDate = getDateOptions(value.table);
                                            setRowDataDateOptions(rowDate);
                                            setValue('rowDataDate', null);
                                        }
                                    }}
                                />
                            </div>
                            <div className="k-d-flex-row">
                                <BasicDropDownController
                                    label={t('labels.rowDataDate')}
                                    control={control}
                                    name="rowDataDate"
                                    data-testid="row-data-date"
                                    data={rowDataDateOptions || []}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                    disabled={disableRowDataFields}
                                />
                            </div>
                            <div className="k-d-flex-row k-gap-3">
                                <div className="k-flex-1">
                                    <BasicDropDownController
                                        label={t('labels.rateBy')}
                                        control={control}
                                        name="rateBy"
                                        data-testid="rate-by"
                                        data={[t('options.PERIOD'), t('options.TIMEPOINTS')]}
                                        required
                                        allowClear
                                        readOnly={!isEditing}
                                        filterable
                                        onChange={async (event) => {
                                            const value = event.value as { id: string; name: string };
                                            if (value) {
                                                setValue(
                                                    'scaleBy',
                                                    ScaleByOptions.find((o) => o.id === ModelScaleBy.PER_SUBJECT_YEAR),
                                                );
                                                setValue(
                                                    'scaleTimepoint',
                                                    ScaleTimePointOptions.find(
                                                        (o) => o.id === ModelScaleBy.PER_SUBJECT_TIME_POINT,
                                                    ),
                                                );
                                            }
                                            setValue('timepointsList', null);
                                            setValue('timepointDate', null);
                                        }}
                                    />
                                </div>
                                <div className="k-flex-1">
                                    {isRateByPeriod && (
                                        <BasicDropDownController
                                            label={t('labels.scaleBy')}
                                            control={control}
                                            name="scaleBy"
                                            data-testid="scale-by"
                                            data={ScaleByOptions || []}
                                            dataItemKey="id"
                                            textField="name"
                                            required
                                            allowClear
                                            readOnly={!isEditing}
                                            filterable
                                        />
                                    )}
                                    {isRateByTimePoints && (
                                        <BasicDropDownController
                                            label={t('labels.scaleTimePoint')}
                                            control={control}
                                            name="scaleTimepoint"
                                            data-testid="scale-timepoint"
                                            data={ScaleTimePointOptions || []}
                                            dataItemKey="id"
                                            textField="name"
                                            required
                                            allowClear
                                            readOnly={!isEditing}
                                            filterable
                                        />
                                    )}
                                </div>
                            </div>
                            {isRateByTimePoints && (
                                <div className="k-d-flex-row k-gap-3">
                                    <BasicDropDownController
                                        label={t('labels.timepointsList')}
                                        control={control}
                                        name="timepointsList"
                                        data-testid="timepoints-list"
                                        data={timePointsLListOptions || []}
                                        dataItemKey="id"
                                        textField="name"
                                        required
                                        allowClear
                                        readOnly={!isEditing}
                                        filterable
                                        onChange={async (event) => {
                                            const { value } = event;
                                            if (value) {
                                                const tp = getDateOptions(value.table);
                                                setTimepointDateOptions(tp);
                                                setValue('timepointDate', null);
                                            }
                                        }}
                                    />
                                </div>
                            )}
                            {isRateByTimePoints && (
                                <div className="k-d-flex-row k-gap-3">
                                    <BasicDropDownController
                                        label={t('labels.timepointDate')}
                                        control={control}
                                        name="timepointDate"
                                        data-testid="timepoint-date"
                                        data={timepointDateOptions || []}
                                        dataItemKey="id"
                                        textField="name"
                                        required
                                        allowClear
                                        readOnly={!isEditing}
                                        filterable
                                        disabled={disableTimePointDateField}
                                    />
                                </div>
                            )}
                            <div className="k-d-flex-row k-gap-3">
                                <div className="k-flex-1">
                                    <BasicDropDownController
                                        label={t('labels.plotBy')}
                                        control={control}
                                        name="plotBy"
                                        data-testid="plot-by"
                                        data={PlotByOptions || []}
                                        dataItemKey="id"
                                        textField="name"
                                        required
                                        allowClear
                                        readOnly={!isEditing}
                                        filterable
                                    />
                                </div>
                                <div className="k-flex-1">
                                    <BasicDropDownController
                                        label={t('labels.plotCalculations')}
                                        control={control}
                                        name="plotCalculations"
                                        data-testid="plot-calculation"
                                        data={PlotCalculationOptions || []}
                                        dataItemKey="id"
                                        textField="name"
                                        required
                                        allowClear
                                        readOnly={!isEditing}
                                        filterable
                                        onChange={async (event) => {
                                            const { value } = event;
                                            if (value && value.id === ModelPlotCalculation.BY_TIME_PERIOD) {
                                                setValue(
                                                    'automationRange',
                                                    RangeOptions.find((i) => i.id === AutomationRange.SPECIFIC_RANGE),
                                                );
                                                setValue('numberOfTimePeriods', 6);
                                            } else {
                                                setValue('automationRange', null);
                                                setValue('numberOfTimePeriods', null);
                                            }

                                            await trigger(['automationRange']);
                                            await trigger(['numberOfTimePeriods']);
                                        }}
                                    />
                                </div>
                            </div>

                            <Card
                                data-testid="tri-event-rates-ot-automation"
                                className="k-min-h-min"
                                title={t('automation')}
                                bodyClassName="!k-pt-2"
                            >
                                <div className="k-d-flex-row k-gap-3">
                                    <div className="k-flex-1">
                                        <BasicDropDownController
                                            label={t('labels.range')}
                                            control={control}
                                            name="automationRange"
                                            data-testid="range"
                                            data={RangeOptions || []}
                                            dataItemKey="id"
                                            textField="name"
                                            allowClear
                                            readOnly={!isEditing}
                                            filterable
                                            disabled={disableAutomationRange}
                                            defaultValue={RangeOptions.find(
                                                (i) => i.id === AutomationRange.SPECIFIC_RANGE,
                                            )}
                                            onChange={async (event) => {
                                                const { value } = event;
                                                if (value && value.id === AutomationRange.SPECIFIC_RANGE) {
                                                    setValue('numberOfTimePeriods', 6);
                                                } else {
                                                    setValue('numberOfTimePeriods', null);
                                                }
                                                await trigger(['numberOfTimePeriods']);
                                            }}
                                            validateRules={{
                                                required: (value) => {
                                                    const plotCalculations = getValues('plotCalculations');
                                                    const range = value?.id as string;
                                                    if (
                                                        plotCalculations != null &&
                                                        plotCalculations.id === ModelPlotCalculation.BY_TIME_PERIOD &&
                                                        range == null
                                                    )
                                                        return t('validation.required');
                                                    return true;
                                                },
                                            }}
                                        />
                                    </div>
                                    <div className="k-flex-1">
                                        <NumericTextBoxController
                                            label={t('labels.noOfTimePeriods')}
                                            control={control}
                                            name="numberOfTimePeriods"
                                            data-testid="number-Of-Time-Periods"
                                            readOnly={!isEditing}
                                            min={1}
                                            validationMessage={t('validation.valueGreaterThan0')}
                                            disabled={!enableNoOfTimePeriods}
                                            validateRules={{
                                                required: (value) => {
                                                    const range = getValues('automationRange');
                                                    const automationPeriods = value as string;
                                                    if (
                                                        range != null &&
                                                        range.id === AutomationRange.SPECIFIC_RANGE &&
                                                        automationPeriods == null
                                                    )
                                                        return t('validation.required');
                                                    return true;
                                                },
                                            }}
                                        />
                                    </div>
                                </div>
                            </Card>

                            <div className="k-d-flex-row k-gap-3">
                                <BasicDropDownController
                                    label={t('labels.calcStartDate')}
                                    control={control}
                                    name="calcStartDate"
                                    data-testid="calc-start-date"
                                    data={
                                        getDateOptions(RowDataSourceTableDto.SUBJECT)?.sort((a, b) =>
                                            a.name.localeCompare(b.name),
                                        ) || []
                                    }
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                />
                            </div>
                            <div className="k-d-flex-row k-gap-3">
                                <MultiSelectController
                                    isEditing
                                    control={control}
                                    name="calcEndDates"
                                    data={
                                        getDateOptions(RowDataSourceTableDto.SUBJECT)?.sort((a, b) =>
                                            a.name.localeCompare(b.name),
                                        ) || []
                                    }
                                    data-testid="calc-end-dates"
                                    label={t('labels.calcEndDate')}
                                    dataItemKey="id"
                                    textField="name"
                                    filterable
                                    disabled={!isEditing}
                                    required
                                />
                            </div>
                        </div>
                        {/* COLUMN 3 */}
                        <div className={cn(styles.borderRight, 'k-d-flex-col k-gap-3 k-overflow-y-auto k-px-4 k-py-3')}>
                            <Card
                                data-testid="tri-event-rates-ot-thresholds"
                                className="k-min-h-min "
                                title={t('thresholds')}
                                bodyClassName="!k-pt-2"
                            >
                                <div className="k-d-flex-row ">
                                    <BasicDropDownController
                                        label={t('labels.thresholdType')}
                                        control={control}
                                        name="thresholdType"
                                        data-testid="threshold-type"
                                        data={ThresholdTypeOptions || []}
                                        dataItemKey="id"
                                        textField="name"
                                        required
                                        allowClear
                                        readOnly={!isEditing}
                                        filterable
                                        onChange={async (event) => {
                                            const value = event.value as { id: string; name: string };
                                            if (value) {
                                                if (value.id !== EventRatesOTThresholdType.FIXED) {
                                                    setValue('upperRed', 3);
                                                    setValue('lowerRed', 3);
                                                    setValue('upperAmber', 2);
                                                    setValue('lowerAmber', 2);
                                                    setValue('studyType', null);
                                                    setValue('countryValue', null);
                                                    setUpdatedBenchMarkOptions(
                                                        BenchmarkTypeOptions.filter(
                                                            (item) => item.id !== EventRatesOTBenchmarkType.NONE,
                                                        ),
                                                    );
                                                } else {
                                                    setValue('upperRed', null);
                                                    setValue('lowerRed', null);
                                                    setValue('upperAmber', null);
                                                    setValue('lowerAmber', null);
                                                    setValue('studyType', null);
                                                    setValue('countryValue', null);
                                                    setUpdatedBenchMarkOptions(BenchmarkTypeOptions);
                                                }
                                            }
                                            await trigger(['upperRed', 'lowerRed', 'upperAmber', 'lowerAmber']);
                                        }}
                                    />
                                </div>
                                <div className="k-d-flex-row k-gap-3 k-mt-2">
                                    <div className="k-flex-1">
                                        <NumericTextBoxController
                                            label={t('labels.upperRed')}
                                            control={control}
                                            name="upperRed"
                                            data-testid="upper-red"
                                            readOnly={!isEditing}
                                            disabled={disableThresholds}
                                            min={0}
                                            validateRules={{
                                                valueOrder: (value: number | null) => {
                                                    const upperAmber = getValues('upperAmber');
                                                    const thresholdType = getValues('thresholdType');
                                                    if (
                                                        upperAmber != null &&
                                                        value != null &&
                                                        (value as number) <= upperAmber
                                                    )
                                                        return t('validation.upperRedThresholdOrder');

                                                    if (
                                                        thresholdType?.id !== EventRatesOTThresholdType.FIXED &&
                                                        value != null &&
                                                        (value as number) <= 0
                                                    ) {
                                                        return t('validation.valueGreaterThan0');
                                                    }

                                                    if (
                                                        thresholdType?.id !== EventRatesOTThresholdType.FIXED &&
                                                        value == null &&
                                                        upperAmber != null
                                                    )
                                                        return t('validation.required');

                                                    return undefined;
                                                },
                                            }}
                                            handleInputChange={async () => {
                                                await trigger(['upperAmber']);
                                            }}
                                        />
                                    </div>
                                    <div className="k-flex-1">
                                        <NumericTextBoxController
                                            label={t('labels.upperAmber')}
                                            control={control}
                                            name="upperAmber"
                                            data-testid="upper-amber"
                                            readOnly={!isEditing}
                                            disabled={disableThresholds}
                                            min={0}
                                            validateRules={{
                                                valueOrder: (value: number | null) => {
                                                    const upperRed = getValues('upperRed');
                                                    const thresholdType = getValues('thresholdType');
                                                    if (
                                                        upperRed != null &&
                                                        value != null &&
                                                        (value as number) >= upperRed
                                                    )
                                                        return t('validation.upperAmberThresholdOrder');

                                                    if (
                                                        thresholdType?.id !== EventRatesOTThresholdType.FIXED &&
                                                        value != null &&
                                                        (value as number) <= 0
                                                    ) {
                                                        return t('validation.valueGreaterThan0');
                                                    }

                                                    if (
                                                        thresholdType?.id !== EventRatesOTThresholdType.FIXED &&
                                                        value == null &&
                                                        upperRed != null
                                                    )
                                                        return t('validation.required');

                                                    return undefined;
                                                },
                                            }}
                                            handleInputChange={async () => {
                                                await trigger('upperRed');
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="k-d-flex-row k-gap-3 k-mt-2">
                                    <div className="k-flex-1">
                                        <NumericTextBoxController
                                            label={t('labels.lowerAmber')}
                                            control={control}
                                            name="lowerAmber"
                                            data-testid="lower-amber"
                                            readOnly={!isEditing}
                                            min={0}
                                            disabled={disableThresholds}
                                            validateRules={{
                                                valueOrder: (value: number | null) => {
                                                    const lowerRed = getValues('lowerRed');
                                                    const threshold = getValues('thresholdType') as {
                                                        id: string;
                                                        name: string;
                                                    };
                                                    if (
                                                        threshold.id === EventRatesOTThresholdType.FIXED &&
                                                        lowerRed != null &&
                                                        value != null &&
                                                        (value as number) <= lowerRed
                                                    ) {
                                                        return t('validation.lowerAmberThresholdFixedOrder');
                                                    }
                                                    if (
                                                        threshold.id !== EventRatesOTThresholdType.FIXED &&
                                                        lowerRed != null &&
                                                        value != null &&
                                                        (value as number) >= lowerRed
                                                    )
                                                        return t('validation.lowerAmberThresholdNotFixedOrder');

                                                    if (
                                                        threshold?.id !== EventRatesOTThresholdType.FIXED &&
                                                        value != null &&
                                                        (value as number) <= 0
                                                    ) {
                                                        return t('validation.valueGreaterThan0');
                                                    }

                                                    if (
                                                        threshold?.id !== EventRatesOTThresholdType.FIXED &&
                                                        value == null &&
                                                        lowerRed != null
                                                    )
                                                        return t('validation.required');

                                                    return undefined;
                                                },
                                            }}
                                            handleInputChange={async () => {
                                                await trigger(['lowerRed', 'thresholdType']);
                                            }}
                                        />
                                    </div>
                                    <div className="k-flex-1">
                                        <NumericTextBoxController
                                            label={t('labels.lowerRed')}
                                            control={control}
                                            name="lowerRed"
                                            data-testid="lower-red"
                                            readOnly={!isEditing}
                                            disabled={disableThresholds}
                                            min={0}
                                            validateRules={{
                                                valueOrder: (value: number | null) => {
                                                    const lowerAmber = getValues('lowerAmber');
                                                    const threshold = getValues('thresholdType') as {
                                                        id: string;
                                                        name: string;
                                                    };
                                                    if (
                                                        threshold.id === EventRatesOTThresholdType.FIXED &&
                                                        lowerAmber != null &&
                                                        value != null &&
                                                        (value as number) >= lowerAmber
                                                    ) {
                                                        return t('validation.lowerRedThresholdFixedOrder');
                                                    }
                                                    if (
                                                        threshold.id !== EventRatesOTThresholdType.FIXED &&
                                                        lowerAmber != null &&
                                                        value != null &&
                                                        (value as number) <= lowerAmber
                                                    )
                                                        return t('validation.lowerRedThresholdNotFixedOrder');

                                                    if (
                                                        threshold?.id !== EventRatesOTThresholdType.FIXED &&
                                                        value != null &&
                                                        (value as number) <= 0
                                                    ) {
                                                        return t('validation.valueGreaterThan0');
                                                    }

                                                    if (
                                                        threshold?.id !== EventRatesOTThresholdType.FIXED &&
                                                        value == null &&
                                                        lowerAmber != null
                                                    )
                                                        return t('validation.required');

                                                    return undefined;
                                                },
                                            }}
                                            handleInputChange={async () => {
                                                await trigger(['lowerAmber', 'thresholdType']);
                                            }}
                                        />
                                    </div>
                                </div>
                            </Card>
                            <Card
                                data-testid="tri-event-rates-ot-thresholds"
                                className="k-min-h-min"
                                title={t('benchmarks')}
                                bodyClassName="!k-pt-2"
                            >
                                <div className="k-d-flex-row k-gap-3">
                                    <div className="k-flex-1">
                                        <BasicDropDownController
                                            label={t('labels.studyType')}
                                            control={control}
                                            name="studyType"
                                            data-testid="study-type"
                                            data={updatedBenchMarkOptions || []}
                                            dataItemKey="id"
                                            textField="name"
                                            required
                                            allowClear
                                            readOnly={!isEditing}
                                            filterable
                                            disabled={disableThresholds}
                                            onChange={async (event) => {
                                                const value = event.value as { id: string; name: string };
                                                const thresholdType = getValues('thresholdType');
                                                if (value && thresholdType) {
                                                    if (
                                                        thresholdType.id === EventRatesOTThresholdType.FIXED &&
                                                        value.id !== EventRatesOTBenchmarkType.STUDY_SPECIFIC
                                                    ) {
                                                        setUpdatedCountryTypeOptions(
                                                            CountryBenchmarkTypeOptions.filter(
                                                                (item) =>
                                                                    item.id !==
                                                                    EventRatesOTCountryBenchmarkType.STUDY_SPECIFIC,
                                                            ),
                                                        );
                                                    } else if (
                                                        thresholdType.id !== EventRatesOTThresholdType.FIXED &&
                                                        value.id === EventRatesOTBenchmarkType.STUDY_SPECIFIC
                                                    ) {
                                                        setUpdatedCountryTypeOptions(
                                                            CountryBenchmarkTypeOptions.filter(
                                                                (item) =>
                                                                    item.id !== EventRatesOTCountryBenchmarkType.NONE,
                                                            ),
                                                        );
                                                    } else if (
                                                        thresholdType.id !== EventRatesOTThresholdType.FIXED &&
                                                        value.id !== EventRatesOTBenchmarkType.STUDY_SPECIFIC
                                                    ) {
                                                        setUpdatedCountryTypeOptions(
                                                            CountryBenchmarkTypeOptions.filter(
                                                                (item) =>
                                                                    item.id !== EventRatesOTCountryBenchmarkType.NONE &&
                                                                    item.id !==
                                                                        EventRatesOTCountryBenchmarkType.STUDY_SPECIFIC,
                                                            ),
                                                        );
                                                    } else {
                                                        setUpdatedCountryTypeOptions(CountryBenchmarkTypeOptions);
                                                    }
                                                }
                                                setValue('countryValue', null);
                                                setValue('studySpecificValue', null);
                                            }}
                                        />
                                    </div>
                                    <div className="k-flex-1">
                                        {showStudySpecific && (
                                            <NumericTextBoxController
                                                label={t('labels.studySpecificValue')}
                                                control={control}
                                                name="studySpecificValue"
                                                data-testid="study-specific-value"
                                                readOnly={!isEditing}
                                                required
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="k-d-flex-row k-gap-3">
                                    <div className="k-flex-1 k-mt-2">
                                        <BasicDropDownController
                                            label={t('labels.countryValue')}
                                            control={control}
                                            name="countryValue"
                                            data-testid="country-value"
                                            data={updatedCountryTypeOptions || []}
                                            dataItemKey="id"
                                            textField="name"
                                            required
                                            allowClear
                                            readOnly={!isEditing}
                                            filterable
                                            disabled={disableCountryType}
                                        />
                                    </div>
                                </div>
                            </Card>
                        </div>
                        {/* COLUMN 4 */}
                        <div
                            className={cn(styles.borderRight, 'k-d-flex-col k-gap-3 k-overflow-y-auto k-pt-3 k-pb-1.5')}
                        >
                            <div className="k-px-4">
                                <Card
                                    data-testid="tri-event-rates-ot-eligibility"
                                    className="k-min-h-min"
                                    title={t('minTimeOnStudy')}
                                    bodyClassName="!k-pt-2"
                                >
                                    <div className="k-d-flex-row k-gap-3 ">
                                        <div className="k-flex-1">
                                            <NumericTextBoxController
                                                label={t('labels.minTimeOnStudyUnit')}
                                                control={control}
                                                name="minTimeUnit"
                                                data-testid="min-time-unit"
                                                readOnly={!isEditing}
                                                min={1}
                                                validateRules={{
                                                    required: (value) => {
                                                        const unit = value as string;
                                                        const period = getValues('minTimePeriod');
                                                        if (period != null && unit == null)
                                                            return t('validation.required');
                                                    },
                                                }}
                                                handleInputChange={async () => {
                                                    await trigger(['minTimePeriod']);
                                                }}
                                                validationMessage={t('validation.valueGreaterThan0')}
                                            />
                                        </div>
                                        <div className="k-flex-1">
                                            <BasicDropDownController
                                                label={t('labels.minTimeOnStudyPeriod')}
                                                control={control}
                                                name="minTimePeriod"
                                                data-testid="min-time-period"
                                                data={MinTimeOnStudyPeriodOptions || []}
                                                dataItemKey="id"
                                                textField="name"
                                                allowClear
                                                readOnly={!isEditing}
                                                filterable
                                                onChange={async () => {
                                                    await trigger(['minTimeUnit']);
                                                }}
                                                validateRules={{
                                                    required: (value) => {
                                                        const period = value?.id as string;
                                                        const unit = getValues('minTimeUnit');
                                                        if (period == null && unit != null)
                                                            return t('validation.required');
                                                        return true;
                                                    },
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="k-d-flex-row k-gap-3 k-mt-2">
                                        <div className="k-flex-1">
                                            <NumericTextBoxController
                                                label={t('labels.minSubjects')}
                                                control={control}
                                                name="minSubjects"
                                                data-testid="min-subjects"
                                                readOnly={!isEditing}
                                                min={1}
                                                validationMessage={t('validation.valueGreaterThan0')}
                                            />
                                        </div>
                                        <div className="k-flex-1" />
                                    </div>
                                </Card>
                            </div>

                            <div className="k-d-flex-row k-gap-3 k-mt-2 k-px-4">
                                <div className="k-flex-1">
                                    <NumericTextBoxController
                                        label={t('labels.expectedVolume')}
                                        control={control}
                                        name="expectedVolume"
                                        data-testid="expected-volume"
                                        readOnly={!isEditing}
                                        min={1}
                                        validationMessage={t('validation.valueGreaterThan0')}
                                    />
                                </div>
                                <div className="k-flex-1">
                                    <NumericTextBoxController
                                        label={t('labels.xAxisPtsToShade')}
                                        control={control}
                                        name="xAxisPtsToShade"
                                        data-testid="x-axis-pts-to-shade"
                                        readOnly={!isEditing}
                                        min={1}
                                        validationMessage={t('validation.valueGreaterThan0')}
                                    />
                                </div>
                            </div>

                            <div className="k-d-flex-row k-gap-3 k-px-4">
                                <TextBoxController
                                    label={t('labels.numeratorName')}
                                    data-testid="numerator-name"
                                    name="numeratorName"
                                    control={control}
                                    required
                                    maxLength={50}
                                    readOnly={!isEditing}
                                />
                            </div>
                            <div className="k-d-flex-row k-gap-3 k-mt--2 k-px-4">
                                <TextBoxController
                                    label={t('labels.denominatorName')}
                                    data-testid="denominator-name"
                                    name="denominatorName"
                                    control={control}
                                    required
                                    maxLength={50}
                                    readOnly={!isEditing}
                                />
                            </div>
                            <dl className={cn(styles.auditInfo)}>
                                <dt>{t('labels.created')}:</dt>
                                <dd>
                                    {panel && (
                                        <>
                                            {stringDateWithoutTimeFromUtcNonNullable(panel.created, 'DD-MMM-YYYY')} by{' '}
                                            {panel.createdBy?.firstName} {panel.createdBy?.lastName}
                                        </>
                                    )}
                                </dd>

                                <dt>{t('labels.updated')}:</dt>
                                <dd>
                                    {panel && (
                                        <>
                                            {stringDateWithoutTimeFromUtcNonNullable(panel.updated, 'DD-MMM-YYYY')} by{' '}
                                            {panel.updatedBy?.firstName} {panel.updatedBy?.lastName}
                                        </>
                                    )}
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>
            )}
            {chip === ChipValues.Preview && (
                <PanelPreview
                    processedConfig={processedConfig}
                    currentConfig={currentConfig as unknown as panel_OpraQueries_panel_Panel}
                    isValidConfig={isValid}
                />
            )}
        </div>
    );

    // Show a regular card if we want to hide the edit button (on the ALL panels tab)
    const CardToUse = source === 'All' ? Card : EditCard;

    return (
        <>
            {showReasonForChange && (
                <PanelReasonForChangeDialog
                    onCancel={() => setShowReasonForChange(false)}
                    onSubmit={async (reasonForChange) => {
                        setShowReasonForChange(false);
                        await submit(reasonForChange);
                    }}
                />
            )}
            <CardToUse
                className="k-h-full"
                title={panel ? `${t('eventRatesOT')}: ${panel.recordId}` : t('eventRatesOT')}
                onEdit={() => {
                    setIsEditing(true);
                }}
                padding="none"
                onCancel={async () => {
                    if (componentHasChanges(COMPONENT_NAME)) {
                        const confirmed = await confirmDiscardChanges();
                        if (!confirmed) return;
                    }
                    setChip(ChipValues.Configuration);
                    setIsEditing(false);
                    reset(defaultFormValues);
                    onCancelOverride?.();
                }}
                onSave={async () => {
                    if (panel && isValid) {
                        setShowReasonForChange(true);
                    } else {
                        await submit();
                    }
                }}
                isEditMode={isEditing}
                data-testid="test-id"
                saveDisabled={isSaving || !isDirty || !isValid}
                disabled={!canEdit}
            >
                {isSaving || isLoading ? <Loading /> : content}
            </CardToUse>
        </>
    );
};

const Wrapper = ({ panelRef, createData, canEdit }: IProps) => {
    const {
        loading: isLoading,
        refetch,
        data,
    } = useQuery<IPanel, IPanelVariables>(GET_PANEL, {
        variables: { panelId: panelRef },
        skip: !panelRef,
        fetchPolicy: 'no-cache',
    });

    const { state } = useLocalState();

    // Getting the processed config to display the preview for previously processed configuration.

    const { data: latestDataset, isLoading: datasetLoading } = useGetLatestDataSet(state?.selectedStudy?.id);
    const latestDatasetDate = latestDataset?.created;

    const { data: processedConfig, loading: isConfigLoading } = useQuery<IPanel, IPanelVariables>(GET_PANEL, {
        variables: { panelId: panelRef, timestamp: latestDatasetDate },
        skip: !panelRef || !latestDatasetDate,
        fetchPolicy: 'no-cache',
    });

    if (isLoading || isConfigLoading || datasetLoading) {
        return <Loading />;
    }

    return (
        <EventRatesOT
            key={data?.panel?.auditId}
            panel={data?.panel ?? undefined}
            onCancelOverride={createData?.onCancel}
            onCreate={async (newPanelRef: string) => {
                await createData?.onCreate(newPanelRef);
            }}
            onUpdate={async () => {
                await refetch();
            }}
            canEdit={canEdit}
            processedConfig={processedConfig?.panel ?? undefined}
        />
    );
};

export default Wrapper;
